import {Subscription} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {Injectable, ApplicationRef} from "@angular/core";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {RecordingService} from "./recording.service";
import {RecordingComparisonService, IRecordingComparisonResult} from "./recording-comparison.service";
import {RecognizerService, IRecognizedSequence} from "../recognition/recognizer.service";
import {CssSentence, CssWord} from "../../content/content-view.objects";
import {ActivityEvent, ACTIVITY_STATUS, ActivityEventData} from "../../event/ActivityEvent";
import {BaseInterventionState, TimingParameters, WordWallInterventionState} from "./recording.interventions.state";
import {IExportDictionary, IVocabularyEntry, ISentence, IWord} from "../../api/data-model";
import {MicrophoneCheckEvent, MICROPHONECHECK_STATUS} from "../../event/MicrophoneCheckEvent";
import {WordWallCompleteEvent} from "../../event/WordWallEvents";
import {EventBus} from "../../event/EventBus";
import {RecordingStatusEvent, RECORDING_STATUS} from "../../event/ReadAndRecordEvents";
import {GlossaryService} from "../glossary/glossary.service";
import {ScrollService} from "../scroll/scroll.service";
import {ClientConfiguration} from "../configuration/client.config";
import {StudentConfiguration} from "../assignment/student.configuration";
import {ContentProgression} from "../../constants";
import {AudioQueueService} from "../audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../../event/AudioQueueEvent";
import {TimerStrategy} from "../configuration/timer.config";
import {filter} from "rxjs/operators";

export enum WordWallRecordingStatus {
    RECOGNIZER_INITIALIZED = 1,
    WORDWALL_PRACTICE_READY = 2,
    WORDWALL_ITEM_SECOND_TRY = 3,
    WORDWALL_PRACTICE_COMPLETE = 4
}

export class WordWallItem {
    private _item: CssSentence;
    private _isComplete: boolean;
    private _isRecording: boolean;

    constructor(vocabularyEntry: IVocabularyEntry, glossaryTextAudio: string, index: number) {
        this._isComplete = false;
        this._isRecording = false;
        // TODO: would be nice to abstract word category to an enum but it is a little tricky
        let word:IWord = {audio: glossaryTextAudio,
            dictionary: vocabularyEntry.dictionary,
            word: vocabularyEntry.word,
            glossary: vocabularyEntry.glossary,
            translations: vocabularyEntry.translations,
            prefix: "",
            separator: "",
            category: "2",
            id: index.toString()};
        let sentence:ISentence = {audio: glossaryTextAudio, id: index.toString(), words: [word]};
        this._item = new CssSentence(sentence, null);
    }
    public get audio():string {
        return this._item ? this._item.audio : "";
    }
    public get text():string {
        return (this._item && this._item.words && this._item.words.length) ? this._item.words[0].word : "";
    }
    public get item():CssSentence {
        return this._item;
    }
    public get word():CssWord {
        return (this._item && this._item.words && this._item.words.length) ? this._item.words[0] : null;
    }
    public get isComplete():boolean {
        return this._isComplete;
    }
    public set isComplete(value: boolean) {
        this._isComplete = value;
    }
    public get isRecording():boolean {
        return this._isRecording;
    }
    public set isRecording(value: boolean) {
        this._isRecording = value;
    }
}

@Injectable()
export class WordWallRecordingService {

    private currentWordWallRecordingStatus:WordWallRecordingStatus;

    public wordWallItems: WordWallItem[] = [];
    private _currentItem: WordWallItem = null;
    private timingParameters:TimingParameters = TimingParameters.build(2, 1);
    private interventionState:BaseInterventionState = null;
    private subscriptions:Subscription[] = [];
    public isGuided: boolean;
    private wordWallDictionary: IExportDictionary;
    private _volumeLevel: number;
    private volumeCallback: Function;
    private _isAnimationActive: boolean;
    private transitionTimer: number;
    private wordWallStarted: boolean = false;
    private wordWallEnded = false;

    constructor(private recognizerService: RecognizerService,
                private recordingService: RecordingService,
                private recordingComparisonService:RecordingComparisonService,
                private scrollService: ScrollService,
                private glossaryService:GlossaryService,
                private log:BugsnagLoggingService,
                private eventBus: EventBus,
                private ref:ApplicationRef,
                private audioQueueService:AudioQueueService,
                private config:ClientConfiguration,
                private studentConfig:StudentConfiguration) {

        this.log.log("WORDWALL SERVICE INIT");
    }

    private startTransition(): void {
        this._isAnimationActive = true;
        if (this.practiceComplete() && this.isAnimationActive && this.glossaryService.isGlossaryOpened()) {
            this.eventBus.next(new ActivityEvent(ACTIVITY_STATUS.activity_end,
                new ActivityEventData({type: "glossary"})));
        }
        this.transitionTimer = window.setTimeout(() => {
            this._isAnimationActive = false;
            this.finishWordWall();
        }, 5000);
    }

    public cancelTransition(): void {
        clearTimeout(this.transitionTimer);
        this._isAnimationActive = false;
    }

    public get isAnimationActive(): boolean {
        return this._isAnimationActive;
    }

    private startPractice(): void {
        this.currentWordWallRecordingStatus = WordWallRecordingStatus.WORDWALL_PRACTICE_READY;
        if (this.wordWallStarted && this.isGuided) {
            this.playRepeatAfterMeAudio();
        } else if (this.isGuided) {
            this._currentItem = this.wordWallItems[0];
            this.wordWallStarted = true;
            this.playGuidedIntroAudio();
        } else {
            this.wordWallStarted = true;
            this.playExploratoryIntroAudio();
        }
    }

    private onRecognizerData(recognized: IRecognizedSequence): void {
        let logString = "";
        recognized.data.forEach((recognizedItem) => {
            logString += recognizedItem.word + ":" + recognizedItem.end.toString() + " ";
        });
        if (!this.recordingService.microphoneCheckActive) {
            this.onRecognize(recognized);
        }
    }

    private onRecognize(recognized: IRecognizedSequence): void {
        let recordingComparisonResult: IRecordingComparisonResult;
        // Normal audio comparison
        recordingComparisonResult = this.recordingComparisonService.getComparisonResult(recognized);
        if (this.recognizerService.isRecording()) {
            if (recordingComparisonResult.id) {
                // since only one item to recognize, if we get an id back, we are done
                this.finishItem();
            } else if (recordingComparisonResult.multiWordInternalProgress) {
                this.interventionState.stop();
                this.interventionState.start();
            }
        }
    }

    private areAllWordsCompleted(): boolean {
        return this.wordWallItems.filter((item) => (item.isComplete)).length === this.wordWallItems.length;
    }

    private finishItem(): void {
        this.pause();
        if (this.volumeCallback) {
            this.volumeCallback(0);
        }
        this._currentItem.isComplete = true;
        this._currentItem.isRecording = false;

        if (this.areAllWordsCompleted()) {
            this.currentWordWallRecordingStatus = WordWallRecordingStatus.WORDWALL_PRACTICE_COMPLETE;
        }

        this.eventBus.next(new AudioQueueEvent([
            new AudioQueueItem(this.config.audioUrl + "SFX/RA_CORRECT.mp3", TranslateAudio.NO,
                () => { this.onItemComplete(); },
                () => { this.onItemComplete(); })
            ], InterruptAudio.YES));
        if (this.ref) {
            this.ref.tick();
        }
    }

    private onItemComplete(): void {
        if (this.isGuided) {
            let newIndex = Number(this._currentItem.item.id) + 1;
            if (newIndex < this.wordWallItems.length && !this.wordWallItems[newIndex].isComplete) {
                // start the next item
                this._currentItem = this.wordWallItems[newIndex];
                this.launchGuidedGlossary();
            }
        }
        // check if all items are complete and we have not played the transition animation yet
        if (this.areAllWordsCompleted()) {
            this.scrollService.scrollWordWallToEnd();
            // just reached complete state; start timer for transition and do animation
            if (!this.transitionTimer) {
                this.startTransition();
            }
        }
    }

    private launchGuidedGlossary(): void {
        let hideCloseButton = true;
        let visualOnly = false;
        this.pause();
        this.launchGlossary(hideCloseButton, visualOnly);
    }

    private onWordWallIntervention(): void {
        this.interventionState.stop();
        if (this.currentWordWallRecordingStatus !== WordWallRecordingStatus.WORDWALL_ITEM_SECOND_TRY) {
            // still on first try, have user retry saying the item
            this.pause();
            this.currentWordWallRecordingStatus = WordWallRecordingStatus.WORDWALL_ITEM_SECOND_TRY;
            this.playRetryAudio();
        } else {
            // we are on second try, just give up.
            this.currentWordWallRecordingStatus = WordWallRecordingStatus.WORDWALL_PRACTICE_READY;
            this.finishItem();
        }

    }

    private playGuidedIntroAudio(): void {
        this.pause();

        this.audioQueueService.reset().subscribe(() => {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_VOCAB_01", TranslateAudio.YES,
                    () => { this.launchGuidedGlossary(); },
                    () => { this.launchGuidedGlossary(); })
            ], InterruptAudio.NO));
        });
    }

    private playExploratoryIntroAudio(): void {
        this.pause();

        this.audioQueueService.reset().subscribe(() => {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_VOCAB_02", TranslateAudio.YES,
                    () => { this.doneExploratoryIntro(); },
                    () => { this.doneExploratoryIntro(); })
            ], InterruptAudio.NO));
        });
    }

    public playRepeatAfterMeAudio(): void {
        // TODO: correct filename. spanish?
        this._currentItem.isRecording = true;

        this.eventBus.next(new AudioQueueEvent([
            new AudioQueueItem("RA_VOCAB_03", TranslateAudio.YES,
                () => { this.playItemAudio(); },
                () => { this.playItemAudio(); })
        ], InterruptAudio.YES));
    }
    public doneExploratoryIntro(): void {
        this.log.log("finished exploratory intro audio, waiting for user action");
    }

    private playRetryAudio(): void {

        this.eventBus.next(new AudioQueueEvent([
            new AudioQueueItem("RA_VOCAB_04", TranslateAudio.YES,
                () => { this.playRepeatAfterMeAudio(); },
                () => { this.playRepeatAfterMeAudio(); })
        ], InterruptAudio.YES));
    }

    private playItemAudio(): void {
        this.log.log("playing item audio, config = " + this.config);
        let url = this.config.audioUrl + this._currentItem.audio;
        this.eventBus.next(new AudioQueueEvent([
            new AudioQueueItem(url, TranslateAudio.NO,
                () => { this.recordWordWall(); },
                () => { this.recordWordWall(); })
        ], InterruptAudio.YES));
    }

    public recordWordWall():void {
        this.recordingComparisonService.createSentence(this._currentItem.item, this.wordWallDictionary);
        this.recognizerService.start(this._currentItem.item, 100, this.volumeCallback);
        this.interventionState.start();
    }

    public get volumeLevel(): number {
        return this._volumeLevel;
    }

    public setVolumeCallback(callback: Function): void {
        this.volumeCallback = callback;
    }

    public launchGlossary(hideCloseButton: boolean, visualOnly: boolean): void {
        // send activity event for glossary
        this.log.log("launching glossary:" + this._currentItem.text);
        this.eventBus.next(new ActivityEvent(ACTIVITY_STATUS.activity_end,
            new ActivityEventData({ type: "glossary" })));
        this.eventBus.next(
            new ActivityEvent(ACTIVITY_STATUS.activity_start, new ActivityEventData({
                type: "glossary",
                word: this._currentItem.word,
                hideCloseButton: hideCloseButton,
                visualOnly: visualOnly,
                automatic: true
            }))
        );
    }

    public injectEntries(vocabularyEntries: IVocabularyEntry[],
                         dictionary: IExportDictionary): void {
        // create the data structure for vocabulary practice.  Glossary data is needed since the glossary item audio
        // is played separately as a prompt; pronunciation dictionary data is needed for the recognizer.
        this.wordWallDictionary = dictionary;
        this.wordWallItems = [];
        if (vocabularyEntries) {
            vocabularyEntries.forEach((entry, index) => {
                let glossaryTextAudio = this.glossaryService.getTextAudio(
                    this.glossaryService.getEntry(entry.glossary));
                this.wordWallItems.push(new WordWallItem(entry, glossaryTextAudio, index));
            });
        }
    }

    public set currentItem(item: WordWallItem) {
        this._currentItem = item;
    }

    public get currentItem(): WordWallItem {
        return this._currentItem;
    }

    public pause():void {
        this.recognizerService.stop({catchErr: false, clear: true, continuousRecording: false});
        this.interventionState.stop();
        // from ReadingComponent
        this.eventBus.next(new RecordingStatusEvent(RECORDING_STATUS.stop));
    }

    public getStatus(): WordWallRecordingStatus {
        return this.currentWordWallRecordingStatus;
    }

    public finishWordWall(): void {
        this.log.log("calling finishWordWall");
        if (this.isActive) {
            this.wordWallStarted = false;
            this.wordWallEnded = true;
            if (this.glossaryService.isGlossaryOpened()) {
                this.eventBus.next(new ActivityEvent(ACTIVITY_STATUS.activity_end,
                    new ActivityEventData({type: "glossary"})));
            } else {
                this.eventBus.next(new WordWallCompleteEvent());
            }
        } else {
            this.log.info("Duplicate call to WordWallRecordingService:finishWordWall");
        }
    }

    public get alwaysAllowStart():boolean {
        return (this.studentConfig.contentProgression === ContentProgression.UNLOCKED);
    }

    public activate(isGuided: boolean):void {
        // TODO: better to pass in guided vs not here, or set in constructor?
        this.log.log("WORDWALL SERVICE ACTIVATE");
        this.deactivate(); // clear subscriptions just in case
        this._volumeLevel = 0;
        this.recordingService.wordWallActive = true;
        this.currentWordWallRecordingStatus = WordWallRecordingStatus.RECOGNIZER_INITIALIZED;
        this.initializeSubscriptions();
        this.isGuided = (this.studentConfig.contentProgression === ContentProgression.UNLOCKED) ? false : isGuided;
        if (this.recordingService.microphoneCheckCompleted) {
            this.startPractice();
        } else {
            this.log.log("wordwall requesting microphone");
            this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.automatic));
        }
    }

    private initializeSubscriptions(): void {
        // timer for how long we wait to hear the word wall term
        this.interventionState = new WordWallInterventionState(this.timingParameters);
        let sub = this.interventionState.status.subscribe((message) => {
            this.log.log("onWordWallIntervention:" + message);
            this.onWordWallIntervention();

        });
        this.subscriptions.push(sub);
        // subscribe to recognizer events
        sub = this.recognizerService.recognizerResultObservable.subscribe(data => {
            this.onRecognizerData(data);
        });
        this.subscriptions.push(sub);
        // If the mic check was needed, start when it is done
        sub = this.eventBus.pipe(filter(
            (e: MicrophoneCheckEvent) => e.type === MicrophoneCheckEvent.TYPE)).subscribe((e: MicrophoneCheckEvent) => {
            if ((e.status === MICROPHONECHECK_STATUS.success) || (e.status === MICROPHONECHECK_STATUS.cancel)) {
                if (!this.wordWallStarted) {
                    this.startPractice();
                }
            }
        });
        this.subscriptions.push(sub);
        // deal with various glossary interactions
        sub = this.eventBus.pipe(filter(event => event instanceof ActivityEvent)).subscribe((event: ActivityEvent) => {
            this.log.log("got activityEvent: " + event.status + JSON.stringify(event.data.timing));
            if (event.status === ACTIVITY_STATUS.activity_end &&
                !!event.data.timing && event.data.timing.key === "glossaryAudio") {
                if (this._currentItem.isRecording) {
                    // if we get this event while recording, it is because the user closed the glossary panel
                    this.pause();
                    this._currentItem.isRecording = false;
                    this.audioQueueService.hardReset();
                    // this.audioPlaybackService.cancel();
                } else if (this.isGuided && !this.currentItem.isComplete) {
                    // Also need to know when the glossary audio is done playing if in guided mode.
                    this.log.log("wordwall finished glossary, playing repeat after me (guided mode");
                    this.playRepeatAfterMeAudio();
                }
            }
            // if word wall is ended, we may be waiting for glossary to be fully closed before transitioning
            if (!!event.data.timing && event.data.timing === TimerStrategy.glossaryEndComplete && this.wordWallEnded) {
                this.log.info("Sending WordWallCompleteEvent after glossary close");
                this.eventBus.next(new WordWallCompleteEvent());
            }
        });
        this.subscriptions.push(sub);
        // if the user clicks to play glossary audio while we are recording, cancel recording
        sub = this.eventBus.pipe(filter((e: ActivityEvent) => e instanceof ActivityEvent))
            .subscribe((event: ActivityEvent) => {
                this.log.log("event " + event.status + " type " + event.data.type + " audio " + event.data.audio +
                             " status " + event.status + " timing " + JSON.stringify(event.data.timing));
                let isGlossaryTiming = !!event.data.timing && (event.data.timing.key === "glossaryAudio" ||
                                                               event.data.timing.key === "glossaryFact");
                let isTranslationInteraction =  event.data.audio && (event.data.audio === "spanish");
                let isGlossaryInteraction = (event.status === ACTIVITY_STATUS.activity_start) &&
                                            (isGlossaryTiming || isTranslationInteraction);
                if (isGlossaryInteraction && this._currentItem.isRecording) {
                    this.log.log("cancelling recording due to glossary audio interaction");
                    this.pause();
                    this._currentItem.isRecording = false;
                    this.audioQueueService.hardReset();
                    // this.audioPlaybackService.cancel();
                }
        });
        this.subscriptions.push(sub);
    }

    public deactivate():void {
        this.log.log("WORDWALL SERVICE DEACTIVATE");
        this.currentWordWallRecordingStatus = WordWallRecordingStatus.RECOGNIZER_INITIALIZED;
        this.recordingService.wordWallActive = false;
        this.wordWallStarted = false;
        this.wordWallEnded = false;
        this.currentItem = null;
        this.wordWallItems.forEach((item) => {
            item.isComplete = false;
            item.isRecording = false;
        });
        if (this.subscriptions.length) {
            this.subscriptions.forEach((sub) => {
                sub.unsubscribe();
            });
        }
        this.subscriptions = [];
    }

    public practiceComplete(): boolean {
        return this.getStatus() === WordWallRecordingStatus.WORDWALL_PRACTICE_COMPLETE;
    }

    public get isRecording(): boolean {
        return this.currentItem && this.currentItem.isRecording;
    }

    public get isActive(): boolean {
        return this.recordingService.wordWallActive;
    }

    public isWordInWordWallSelected(): boolean {
        let inGuidedSequence = this.isGuided && !this.practiceComplete();
        return (inGuidedSequence || this.isRecording);
    }

}
